import React, { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory } from 'react-router';
import { getEsy } from '../../../api/services/esyService';
import { updateEsy } from '../../../api/services/esyService';
import { deleteEsy } from '../../../api/services/esyService';
import { getDivisions } from '../../../api/services/divisionService';
import { ResponseStatus } from '../../../components/notifications';
import { FieldInput } from '../../../components/fieldInput';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/layout';
import LayoutError from '../../../components/layoutError';
import SubmitButton from '../../../components/submitButton';
import cx from 'classnames';
import Modal from '../../../components/modal';

type Params = {
    id: string;
};

const fetchESY = async ({ id }: any) => {
    return await getEsy(id);
};


const EditESYs = () => {
    const { id } = useParams<Params>();
    const history = useHistory();

    // State
    const [esyId, setEsyId] = useState<number | undefined>();
    const [name, setName] = useState('');
    const [divisions, setDivisions] = useState<number[]>([]);
    const [modalDivisions, setModalDivisions] = useState<number[]>([]);
    const [openDivisions, setOpenDivisions] = useState(false);

    const divisionData = useAsync({
        promiseFn: getDivisions,
    });

    const ESYData = useAsync({
        promiseFn: fetchESY,
        id,
        onResolve: (r) => {
            setEsyId(r.data.esy.esyId);
            setName(r.data.esy.name);
            setDivisions(
                r.data.esy.esyDivisions.map((d) => d.divisionId)
            );
        },
    });

    const saveEsy = async () => {
        if (!esyId) {
            return Promise.reject('Missing esyId.');
        }

        return await updateEsy(esyId, {
            name: name,
            divisions: divisions
        });
    };

    const removeEsy = async () => {
        if (!esyId) {
            return Promise.reject('Missing esyId.');
        }

        return await deleteEsy(esyId);
    };

    const save = useAsync({
        deferFn: saveEsy,
        onResolve: () => {
            history.push('/admin/esys', {
                message: `"${name}" saved successfully!`,
            });
        },
        onReject: () => {
            window.scrollTo(0, 0);
        },
    });

    // Error handling
    if (ESYData.error || divisionData.error) return <LayoutError />;

    const handleRemoveDivision = (id?: number) => {
        if (!id) return;

        setDivisions([...divisions.filter((d) => d !== id)]);
    };

    const handleModalDivisionChange = (
        e: React.FormEvent<HTMLInputElement>
    ) => {
        if (e.currentTarget.checked) {
            setModalDivisions([
                ...modalDivisions,
                parseInt(e.currentTarget.value),
            ]);
        } else {
            setModalDivisions([
                ...modalDivisions.filter(
                    (d) => d !== parseInt(e.currentTarget.value)
                ),
            ]);
        }
    };

    // Misc
    const SelectedDivisions =
        divisionData.data?.data.divisions.filter((d) =>
            divisions.includes(d.divisionId ?? -1)
        ) ?? [];

    const handleSubmit = () => {
        save.run();
    };

    return (
        <Fragment>
            <Modal
                open={openDivisions}
                onCancel={() => {
                    setOpenDivisions(false);
                }}
                onSave={() => {
                    setDivisions(modalDivisions);
                    setOpenDivisions(false);
                }}
            >
                <Fragment>
                    <div className='flex'>
                        <div className='flex'>
                            <div className='flex items-center h-5 ml-1'>
                                <input
                                    name='all-divisions'
                                    type='checkbox'
                                    checked={
                                        divisionData.data?.data.divisions.length ===
                                        modalDivisions.length
                                    }
                                    onChange={(e) => {
                                        if (e.currentTarget.checked) {
                                            const d =
                                                divisionData.data?.data.divisions.map(
                                                    (d) => d.divisionId ?? -1
                                                ) ?? [];

                                            setModalDivisions(d);
                                        } else {
                                            setModalDivisions([]);
                                        }
                                    }}
                                />
                            </div>
                            <div className='ml-3 text-sm'>
                                <label htmlFor='all-divisions'>Select All</label>
                            </div>
                        </div>
                    </div>
                    <hr className='my-4' />
                    <div className="max-h-48 overflow-y-auto">
                        <div className='flex flex-col'>
                            {divisionData.data?.data.divisions.map((d, i) => {
                                const dip = `division_${i}`;

                                return (
                                    <div key={dip} className='flex items-start py-1'>
                                        <div className='flex items-center h-5 ml-1'>
                                            <input
                                                id={dip}
                                                name='division'
                                                type='checkbox'
                                                value={d.divisionId}
                                                checked={modalDivisions.includes(d.divisionId ?? -1)}
                                                onChange={handleModalDivisionChange}
                                            />
                                        </div>
                                        <div className='ml-3 text-sm'>
                                            <label htmlFor={dip}>{d.name}</label>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Fragment>
            </Modal>
            <Layout isLoading={ESYData.isPending || divisionData.isPending}>
                <Fragment>
                    <ResponseStatus state={save} />
                    <div className='grid grid-cols-6 gap-6 mb-6'>
                        <div className='col-span-6 sm:col-span-6'>
                            <FieldInput
                                name='esy'
                                label='Name'
                                minLength={1}
                                onChange={(e) => setName(e.target.value)}
                                value={name}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-6'>
                            <div className='flex mb-3 justify-between'>
                                <div>
                                    <label>Divisions</label>
                                    <p className='text-sm text-gray-500'>
                                        Select all applicable divisions.
                                    </p>
                                </div>
                                <div className='flex items-end'>
                                    <button
                                        className='callout'
                                        onClick={() => {
                                            setModalDivisions(divisions);
                                            setOpenDivisions(true);
                                        }}
                                    >
                                        <svg
                                            xmlns='http://www.w3.org/2000/svg'
                                            className='h-5 w-5'
                                            viewBox='0 0 20 20'
                                            fill='currentColor'
                                        >
                                            <path d='M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z' />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className='mt-1 rounded-lg bg-white shadow overflow-hidden'>
                                {(SelectedDivisions.length === 0 ||
                                    divisions.length ===
                                    divisionData.data?.data.divisions.length) && (
                                        <div className='py-3 px-5'>
                                            <div className='text-base font-normal'>Global</div>
                                            <p className='text-sm text-gray-500'>
                                                ESY will appear for all divisions.
                                            </p>
                                        </div>
                                    )}

                                {SelectedDivisions.length !== 0 &&
                                    divisions.length !==
                                    divisionData.data?.data.divisions.length &&
                                    SelectedDivisions.map((d, i) => {
                                        return (
                                            <div
                                                key={d.divisionId}
                                                className={cx(
                                                    'flex items-start justify-between py-3 px-5',
                                                    i % 2 === 0 ? null : 'bg-gray-50'
                                                )}
                                            >
                                                <div className='text-sm'>{d.name}</div>
                                                <svg
                                                    xmlns='http://www.w3.org/2000/svg'
                                                    className='h-6 w-6 text-red-500 cursor-pointer'
                                                    onClick={() => handleRemoveDivision(d.divisionId)}
                                                    viewBox='0 0 20 20'
                                                    fill='currentColor'
                                                >
                                                    <path
                                                        fillRule='evenodd'
                                                        d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                                                        clipRule='evenodd'
                                                    />
                                                </svg>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                    <hr className='py-4' />
                    <div className='text-right'>
                        <SubmitButton
                            text='Save Changes'
                            disabled={
                                name.length === 0
                            }
                            spinning={save.isPending}
                            onClick={handleSubmit}
                        />
                    </div>
                </Fragment>
            </Layout>
        </Fragment>
    );
};

export default EditESYs;
